import React, { useState, useEffect } from 'react';
import { 
  Webhook, Key, Plus, Copy, Trash2, Eye, EyeOff, 
  CheckCircle, AlertCircle, Clock, RefreshCw, Loader2,
  ExternalLink, Settings, Shield, Activity, ChevronDown, ChevronUp,
  Bell, Mail, Smartphone, Moon, Send, TestTube
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('rooftrack_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WebhookManagement = () => {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKey, setShowNewKey] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(true);
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [showNotificationSettings, setShowNotificationSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadData();
      loadNotificationSettings();
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [keysRes, infoRes, logsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/webhooks/keys`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/webhooks/info`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/webhooks/logs?limit=20`, { headers: getAuthHeaders() })
      ]);
      setApiKeys(keysRes.data.keys || []);
      setWebhookInfo(infoRes.data);
      setLogs(logsRes.data.logs || []);
    } catch (err) {
      console.error('Failed to load webhook data:', err);
      toast.error('Failed to load webhook configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/notifications/settings`,
        { headers: getAuthHeaders() }
      );
      setNotificationSettings(response.data);
    } catch (err) {
      console.error('Failed to load notification settings:', err);
    }
  };

  const updateNotificationSetting = async (field, value) => {
    try {
      setSavingSettings(true);
      await axios.put(
        `${BACKEND_URL}/api/notifications/settings`,
        { [field]: value },
        { headers: getAuthHeaders() }
      );
      setNotificationSettings(prev => ({ ...prev, [field]: value }));
      toast.success('Settings updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const sendTestNotification = async (type) => {
    try {
      if (type === 'email') setTestingEmail(true);
      if (type === 'sms') setTestingSms(true);
      
      await axios.post(
        `${BACKEND_URL}/api/notifications/test`,
        { type },
        { headers: getAuthHeaders() }
      );
      toast.success(`Test ${type} notification sent!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to send test ${type}`);
    } finally {
      setTestingEmail(false);
      setTestingSms(false);
    }
  };

  const generateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/webhooks/keys/generate`,
        { name: newKeyName },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setShowNewKey(response.data.api_key);
        setNewKeyName('');
        await loadData();
        toast.success('API key generated! Copy it now - it won\'t be shown again.');
      }
    } catch (err) {
      toast.error('Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const revokeApiKey = async (keyId, keyName) => {
    if (!confirm(`Are you sure you want to revoke "${keyName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/webhooks/keys/${keyId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('API key revoked');
      await loadData();
    } catch (err) {
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const activeKeys = apiKeys.filter(k => k.is_active);
  const totalLeadsReceived = apiKeys.reduce((sum, k) => sum + (k.total_leads_received || 0), 0);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="webhook-management-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Webhook className="w-7 h-7 text-purple-600" />
          Webhook Integration
        </h1>
        <p className="text-gray-600 mt-1">
          Connect your lead providers (GoHighLevel, Zapier, etc.) to automatically receive leads
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
          <div className="flex items-center gap-2 text-purple-700 mb-2">
            <Key className="w-5 h-5" />
            <span className="font-medium">Active API Keys</span>
          </div>
          <div className="text-2xl font-bold text-purple-600">{activeKeys.length}</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
          <div className="flex items-center gap-2 text-green-700 mb-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Leads Received</span>
          </div>
          <div className="text-2xl font-bold text-green-600">{totalLeadsReceived}</div>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center gap-2 text-blue-700 mb-2">
            <Activity className="w-5 h-5" />
            <span className="font-medium">Recent Activity</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">{logs.length}</div>
          <div className="text-xs text-blue-500">Last 20 webhooks</div>
        </div>
      </div>

      {/* Webhook URL Card */}
      {webhookInfo && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-blue-600" />
            Your Webhook URL
          </h2>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <code className="text-sm text-gray-800 break-all">
                {webhookInfo.webhook_urls.ghl}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(webhookInfo.webhook_urls.ghl, 'Webhook URL')}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Use this URL in GoHighLevel, Zapier, or any lead provider that supports webhooks.
          </p>
        </div>
      )}

      {/* Notification Settings */}
      {notificationSettings && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
          <button
            onClick={() => setShowNotificationSettings(!showNotificationSettings)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
          >
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-600" />
              Lead Notifications
            </h2>
            {showNotificationSettings ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showNotificationSettings && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-6">
              <p className="text-sm text-gray-600">
                Get notified instantly when new leads arrive via webhook
              </p>

              {/* Email Notifications */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Email Notifications</h3>
                      <p className="text-sm text-gray-500">Receive lead details via email</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={notificationSettings.email_notifications}
                      onChange={(e) => updateNotificationSetting('email_notifications', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                {notificationSettings.email_notifications && (
                  <div className="flex gap-3">
                    <Input
                      type="email"
                      placeholder="Email address for notifications"
                      value={notificationSettings.notification_email || ''}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, notification_email: e.target.value }))}
                      onBlur={(e) => updateNotificationSetting('notification_email', e.target.value)}
                      className="flex-1 bg-white"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => sendTestNotification('email')}
                      disabled={testingEmail || !notificationSettings.notification_email}
                    >
                      {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      <span className="ml-1">Test</span>
                    </Button>
                  </div>
                )}
              </div>

              {/* SMS Notifications */}
              <div className={`rounded-lg p-4 ${notificationSettings.sms_available ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${notificationSettings.sms_available ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Smartphone className={`w-5 h-5 ${notificationSettings.sms_available ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 flex items-center gap-2">
                        SMS Notifications
                        {!notificationSettings.sms_available && (
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">Coming Soon</span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {notificationSettings.sms_available 
                          ? 'Get instant text alerts for new leads'
                          : 'Contact support to enable SMS notifications'}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={notificationSettings.sms_notifications}
                      onChange={(e) => updateNotificationSetting('sms_notifications', e.target.checked)}
                      disabled={!notificationSettings.sms_available}
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${notificationSettings.sms_available ? 'peer-checked:bg-green-600 peer-focus:ring-4 peer-focus:ring-green-300' : 'opacity-50 cursor-not-allowed'}`}></div>
                  </label>
                </div>
                
                {notificationSettings.sms_notifications && notificationSettings.sms_available && (
                  <div className="flex gap-3">
                    <Input
                      type="tel"
                      placeholder="Phone number for SMS (e.g., 555-123-4567)"
                      value={notificationSettings.notification_phone || ''}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, notification_phone: e.target.value }))}
                      onBlur={(e) => updateNotificationSetting('notification_phone', e.target.value)}
                      className="flex-1 bg-white"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => sendTestNotification('sms')}
                      disabled={testingSms || !notificationSettings.notification_phone}
                    >
                      {testingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      <span className="ml-1">Test</span>
                    </Button>
                  </div>
                )}
              </div>

              {/* Quiet Hours */}
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <Moon className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Quiet Hours</h3>
                      <p className="text-sm text-gray-500">Pause notifications during specific hours</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={notificationSettings.quiet_hours_enabled}
                      onChange={(e) => updateNotificationSetting('quiet_hours_enabled', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
                
                {notificationSettings.quiet_hours_enabled && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">From</span>
                    <Input
                      type="time"
                      value={notificationSettings.quiet_hours_start || '22:00'}
                      onChange={(e) => updateNotificationSetting('quiet_hours_start', e.target.value)}
                      className="w-28 bg-white"
                    />
                    <span className="text-sm text-gray-600">to</span>
                    <Input
                      type="time"
                      value={notificationSettings.quiet_hours_end || '08:00'}
                      onChange={(e) => updateNotificationSetting('quiet_hours_end', e.target.value)}
                      className="w-28 bg-white"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Key Modal */}
      {showNewKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Key className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">API Key Generated!</h3>
                <p className="text-sm text-gray-600">Copy this key now - it won't be shown again</p>
              </div>
            </div>
            
            <div className="bg-gray-900 rounded-lg p-4 mb-4">
              <code className="text-green-400 text-sm break-all select-all">{showNewKey}</code>
            </div>
            
            <div className="flex gap-3">
              <Button
                className="flex-1"
                onClick={() => copyToClipboard(showNewKey, 'API Key')}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy API Key
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowNewKey(null)}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Section */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            API Keys
          </h2>
        </div>

        {/* Generate New Key */}
        <div className="flex gap-3 mb-6">
          <Input
            placeholder="Key name (e.g., 'GHL Production', 'Zapier')"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            className="flex-1"
          />
          <Button onClick={generateApiKey} disabled={generating}>
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Generate Key
          </Button>
        </div>

        {/* Keys List */}
        {apiKeys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No API keys yet</p>
            <p className="text-sm">Generate one to start receiving leads via webhook</p>
          </div>
        ) : (
          <div className="space-y-3">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  key.is_active 
                    ? 'bg-gray-50 border-gray-200' 
                    : 'bg-red-50 border-red-200 opacity-60'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    key.is_active ? 'bg-purple-100' : 'bg-red-100'
                  }`}>
                    <Key className={`w-5 h-5 ${key.is_active ? 'text-purple-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {key.name}
                      {!key.is_active && (
                        <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                          Revoked
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      <code className="bg-gray-200 px-1 rounded">{key.key_prefix}...</code>
                      <span className="mx-2">•</span>
                      Created {formatDate(key.created_at)}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {key.total_leads_received || 0} leads received
                      {key.last_used && ` • Last used ${formatDate(key.last_used)}`}
                    </div>
                  </div>
                </div>
                
                {key.is_active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revokeApiKey(key.id, key.name)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Setup Guide */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
        <button
          onClick={() => setShowSetupGuide(!showSetupGuide)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
        >
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-600" />
            GoHighLevel Setup Guide
          </h2>
          {showSetupGuide ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {showSetupGuide && webhookInfo && (
          <div className="px-6 pb-6 border-t border-gray-100 pt-4">
            <ol className="space-y-4">
              {webhookInfo.ghl_setup_instructions.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-gray-700">{step.replace(/^\d+\.\s*/, '')}</span>
                </li>
              ))}
            </ol>
            
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-800 mb-2">Example Payload</h4>
              <pre className="text-xs text-yellow-700 overflow-x-auto">
{JSON.stringify(webhookInfo.example_payload, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Activity Logs */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowLogs(!showLogs)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
        >
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-600" />
            Recent Webhook Activity
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); loadData(); }}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {showLogs ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>
        
        {showLogs && (
          <div className="border-t border-gray-100">
            {logs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No webhook activity yet</p>
                <p className="text-sm">Activity will appear here when leads are received</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <div key={log.id} className="px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {log.status === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <div className="text-sm text-gray-900">
                          {log.status === 'success' ? 'Lead received' : 'Error'}
                          <span className="text-gray-500 ml-2">from {log.source}</span>
                        </div>
                        {log.lead_id && (
                          <div className="text-xs text-gray-500">Lead ID: {log.lead_id}</div>
                        )}
                        {log.error && (
                          <div className="text-xs text-red-500">{log.error}</div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(log.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebhookManagement;
